import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import { useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faHouse, faKey } from "@fortawesome/free-solid-svg-icons";

export default function Evaluation() {
  const [popup, setPopup] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    city: "",
    provinceState: "",
    propertySize: "",
    lotSize: "",
    propertyType: "",
    buildingType: "",
    notes: "",
  });

  const seoConfig = pageSEO.evaluation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Home Evaluation",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      setPopup(
        data?.success
          ? {
            subject: "Thank You!",
            message: "Your evaluation request has been received. We will contact you shortly.",
          }
          : {
            subject: "Something went wrong",
            message: "Please try again later.",
          }
      );
    } catch {
      setPopup({
        subject: "Something went wrong",
        message: "Please try again later.",
      });
    }
  }

  const inputClass =
    "w-full rounded-2xl border border-harbour-border bg-[#F8FAFC] px-5 py-4 text-sm outline-none transition focus:border-harbour-accent focus:bg-white";

  return (
    <>
      <SEO {...seoConfig} />

      <section className="relative overflow-hidden bg-[#F8FAFC] pt-36 pb-24">
        <div className="absolute right-0 top-0 h-[500px] w-[500px] rounded-full bg-harbour-blue/5 blur-3xl" />
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative mx-auto max-w-[1400px] px-6"
        >
          <div className="mb-16 text-center">
            <motion.div
              variants={fadeUp}
              className="flex items-center justify-center gap-4"
            >
              <div className="h-px w-14 bg-harbour-accent" />
              <span className="uppercase tracking-[0.5em] text-harbour-blue">
                Complimentary Service
              </span>

              <div className="h-px w-14 bg-harbour-accent" />
            </motion.div>


            <motion.h1
              variants={fadeUp}
              className="mt-8 font-serif text-5xl text-harbour-navy md:text-6xl"
            >
              Discover Your
              <br />
              Home's True Value
            </motion.h1>


            <motion.p
              variants={fadeUp}
              className="mx-auto mt-8 max-w-2xl text-lg leading-8 text-harbour-muted"
            >
              Receive a personalized market evaluation based on current trends,
              comparable properties, and expert local knowledge.
            </motion.p>
          </div>

          <motion.div
            variants={fadeUp}
            className="grid gap-12 lg:grid-cols-[1fr_420px]"
          >
            <div className="rounded-[35px] bg-white p-8 shadow-xl md:p-12">
              <h2 className="font-serif text-3xl text-harbour-navy">
                Request Your Evaluation
              </h2>

              <p className="mt-3 text-harbour-muted">
                Tell us about your property and we will prepare your
                complimentary evaluation.
              </p>

              <form onSubmit={handleSubmit} className="mt-10 space-y-5">
                <input
                  required
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  className={inputClass}
                />

                <div className="grid gap-5 md:grid-cols-2">
                  <input
                    required
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    className={inputClass}
                  />

                  <input
                    required
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number"
                    className={inputClass}
                  />
                </div>

                <input
                  required
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleInputChange}
                  placeholder="Property Address"
                  className={inputClass}
                />

                <div className="grid gap-5 md:grid-cols-2">
                  <input
                    required
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className={inputClass}
                  />

                  <input
                    required
                    name="provinceState"
                    value={formData.provinceState}
                    onChange={handleInputChange}
                    placeholder="Province / State"
                    className={inputClass}
                  />
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <input
                    required
                    name="propertySize"
                    value={formData.propertySize}
                    onChange={handleInputChange}
                    placeholder="Property Size"
                    className={inputClass}
                  />

                  <input
                    required
                    name="lotSize"
                    value={formData.lotSize}
                    onChange={handleInputChange}
                    placeholder="Lot Size"
                    className={inputClass}
                  />
                </div>

                <select
                  required
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Property Type</option>
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Land</option>
                  <option>Investment</option>
                </select>

                <select
                  required
                  name="buildingType"
                  value={formData.buildingType}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Building Type</option>
                  <option>House</option>
                  <option>Townhouse</option>
                  <option>Condo</option>
                  <option>Duplex</option>
                </select>

                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional Information"
                  className={`${inputClass} h-32 resize-none`}
                />

                <button className="btn-harbour mt-3">
                  Request Evaluation
                </button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="overflow-hidden rounded-[35px] shadow-xl">
                <img
                  src={house}
                  alt="Home Evaluation"
                  className="h-[550px] w-full object-cover transition duration-700 hover:scale-105"
                />
              </div>

              <div className="rounded-[35px] bg-harbour-navy p-8 text-white">
                <h3 className="font-serif text-3xl">
                  Why Evaluate Your Home?
                </h3>

                <p className="mt-5 leading-8 text-white/70">
                  Understanding your property's value helps you make confident
                  decisions whether you are preparing to sell, planning ahead,
                  or simply curious about your market position.
                </p>

                <div className="mt-8 space-y-5 text-sm">
                  <div className="flex items-center gap-4">
                    <FontAwesomeIcon icon={faChartLine} className="text-harbour-accent" />
                    Accurate Market Insights
                  </div>

                  <div className="flex items-center gap-4">
                    <FontAwesomeIcon icon={faHouse} className="text-harbour-accent" />
                    Property Specific Analysis
                  </div>

                  <div className="flex items-center gap-4">
                    <FontAwesomeIcon icon={faKey} className="text-harbour-accent" />
                    Expert Guidance
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {popup && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

          <div className="fixed left-1/2 top-1/2 z-50 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white p-10 text-center shadow-2xl">
            <h3 className="font-serif text-3xl text-harbour-navy">
              {popup.subject}
            </h3>

            <p className="mt-4 leading-7 text-harbour-muted">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="btn-harbour mt-8"
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
